/**
 * @fileoverview ChoiceQuestionRenderer component for the Storyteller web application.
 * Displays a choice question with radio buttons.
 *
 * @example
 * // Basic usage
 * <ChoiceQuestionRenderer schema={schema} uischema={uischema} />
 */

import { ControlProps, rankWith, uiTypeIs } from '@jsonforms/core'
import { withJsonFormsEnumProps } from '@jsonforms/react'
import { VanillaRendererProps, withVanillaControlProps } from '@jsonforms/vanilla-renderers'
import { useTranslation } from 'react-i18next'
import { Text } from '../../../typography/Typography'
import css from './ChoiceQuestionRenderer.module.css'

/**
 * @function classNames
 * @description Combines multiple class names into a single string.
 * @param {...string[]} classes - The class names to combine.
 * @returns {string} The combined class names.
 */
function classNames(...classes: string[]) {
    return classes.filter(Boolean).join(' ')
}

/**
 * @function ChoiceQuestion
 * @description Component for rendering a choice question with radio buttons.
 * @param {ControlProps & VanillaRendererProps} props - The props for the ChoiceQuestion component.
 * @returns {JSX.Element} The ChoiceQuestion component.
 */
function ChoiceQuestion({
    schema,
    handleChange,
    errors,
    path,
    data = {},
}: ControlProps & VanillaRendererProps) {
    const { t } = useTranslation()
    const hasError = errors.length > 0

    return (
        <>
            <div className={css.choice_container}>
                <div className={classNames(css.choice_container, hasError && css.error)}>
                    {schema.oneOf?.map(({ const: value, title }) => (
                        <button
                            className={css.button}
                            tabIndex={-1}
                            key={title}
                            onClick={() => handleChange(path, value)}
                        >
                            <input
                                id={path + title}
                                className={css.radio_input}
                                tabIndex={0}
                                type="radio"
                                value={value}
                                checked={data === value}
                                name={path}
                                onChange={() => handleChange(path, value)}
                            />
                            <label htmlFor={path + title}>
                                <Text>{t(title)}</Text>
                            </label>
                        </button>
                    ))}
                </div>
            </div>
            {hasError && <div className={css.error_message}>{errors}</div>}
        </>
    )
}

export const ChoiceQuestionTester = rankWith(3, uiTypeIs('ChoiceQuestion'))

export default withVanillaControlProps(withJsonFormsEnumProps(ChoiceQuestion))
